<template>
    <PopupTemplate
        v-if="shouldShow"
        v-model="isOpen"
        :img-raw="imgSrc"
        :img-alt="t('vue.free_plan_max_reached_popup_img_alt')"
        :title="t('vue.free_plan_max_reached_popup_title')"
        @close="handleClose"
    >
        <template #body>
            <div class="free-plan-max-reached-popup-body">
                <p class="free-plan-max-reached-popup-description">
                    {{
                        t(
                            "vue.free_plan_max_reached_popup_description_primary",
                            {
                                date: renewalDateLabel,
                            },
                        )
                    }}
                </p>
                <p class="free-plan-max-reached-popup-description">
                    {{
                        t(
                            "vue.free_plan_max_reached_popup_description_secondary",
                        )
                    }}
                </p>
            </div>
        </template>

        <template #actions>
            <AtomicButton
                color="light-blue"
                size="lg"
                class="h-10 px-4 text-sm leading-10"
                @click="waitForRenewal"
            >
                {{ t("vue.free_plan_max_reached_popup_wait_button") }}
            </AtomicButton>
            <AtomicButton
                color="yellow"
                size="lg"
                class="h-10 border-transparent bg-[#088BEA] px-4 text-sm leading-10 text-white hover:text-gray-100 focus:ring-[#088BEA] disabled:bg-gray-300 disabled:text-white"
                @click="upgradeToScale"
            >
                {{ t("vue.free_plan_max_reached_popup_upgrade_button") }}
            </AtomicButton>
        </template>
    </PopupTemplate>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { router, usePage } from "@inertiajs/vue3";
import route from "ziggy-js";

import PopupTemplate from "@/components/Atomic/Dialogs/PopupTemplate.vue";
import AtomicButton from "@/components/Atomic/Buttons/AtomicButton.vue";
import { useTranslations } from "@/composables/useTranslations.ts";
import { useFreePlanMaxReachedPopup } from "@/composables/useFreePlanMaxReachedPopup";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps.ts";
import imgSrc from "@/assets/images/free-plan-max-reach.svg?raw";

const { t } = useTranslations();
const page = usePage<IRhizomPageProps>();
const isOpen = ref(false);

const { showFreePlanReportsLimitBlock, freePlanResetDateLabel } =
    useFreePlanMaxReachedPopup();
const shouldShow = showFreePlanReportsLimitBlock;
const renewalDateLabel = freePlanResetDateLabel;

const popupStorageKey = computed(() => {
    const teamUuid = page.props.team?.uuid ?? "global";

    return `ui:free_plan_max_reached_popup_dismissed_until:${teamUuid}`;
});

watch(
    showFreePlanReportsLimitBlock,
    (show) => {
        if (!show) {
            isOpen.value = false;
            return;
        }

        isOpen.value = !isDismissed();
    },
    { immediate: true },
);

watch(isOpen, (openNow) => {
    if (!openNow && showFreePlanReportsLimitBlock.value) {
        dismissForTtl();
    }
});

function waitForRenewal() {
    isOpen.value = false;
    dismissForTtl();
}

function upgradeToScale() {
    isOpen.value = false;
    dismissForTtl();
    router.visit(route("billing.subscriptions.index"));
}

function handleClose() {
    dismissForTtl();
}

const TTL_MS = 1000 * 60 * 60;

function isDismissed(): boolean {
    const until = Number(localStorage.getItem(popupStorageKey.value) ?? 0);

    return Number.isFinite(until) && until > Date.now();
}

function dismissForTtl() {
    localStorage.setItem(popupStorageKey.value, String(Date.now() + TTL_MS));
}
</script>

<style scoped>
:deep(.modal-title) {
    max-width: 360px;
    margin-left: auto;
    margin-right: auto;
    text-align: center;
}

:deep(.modal-body) {
    max-width: 470px !important;
}

.free-plan-max-reached-popup-body {
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.free-plan-max-reached-popup-description {
    color: #4b5563;
    text-align: center;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 142%;
}
</style>
